import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { PlayersAPI } from '../api';
import { useToast } from '../hooks/use-toast';

const AGE_GROUPS = ['U8', 'U10', 'U12', 'U14', 'U16', 'U19', 'U22', 'College', 'Pro'];
const COLUMNS = ['firstName', 'lastName', 'team', 'jerseyNumber', 'position', 'shot', 'ageGroup'];

const SAMPLE_ROWS = [
  { firstName: 'Ethan', lastName: 'Miller', team: 'Frost Wolves U18', jerseyNumber: 17, position: 'Forward', shot: 'L', ageGroup: 'U19' },
  { firstName: 'Liam',  lastName: 'Anderson', team: 'Frost Wolves U18', jerseyNumber: 4,  position: 'Defense', shot: 'R', ageGroup: 'U19' },
  { firstName: 'Mason', lastName: 'Roy',      team: 'Frost Wolves U18', jerseyNumber: 30, position: 'Goalie',  shot: 'L', ageGroup: 'U19' },
];

function downloadTemplate() {
  const ws = XLSX.utils.json_to_sheet(SAMPLE_ROWS, { header: COLUMNS });
  // add a hint row at the bottom via a second sheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Players');

  const help = [
    ['Column', 'Required', 'Allowed values'],
    ['firstName', 'Yes', 'Text'],
    ['lastName', 'Yes', 'Text'],
    ['team', 'Yes', 'Text (used to match existing players for updates)'],
    ['jerseyNumber', 'No', 'Whole number'],
    ['position', 'Yes', 'Forward | Defense | Goalie'],
    ['shot', 'Yes', 'L or R'],
    ['ageGroup', 'Yes', AGE_GROUPS.join(' | ')],
  ];
  const hs = XLSX.utils.aoa_to_sheet(help);
  XLSX.utils.book_append_sheet(wb, hs, 'Instructions');

  XLSX.writeFile(wb, 'ShiftNotes_Players_Template.xlsx');
}

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const inputRef = useRef();
  const { toast } = useToast();

  const reset = () => { setRows([]); setFileName(''); setResult(null); if (inputRef.current) inputRef.current.value = ''; };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      // Normalize keys (case-insensitive)
      const norm = json.map(r => {
        const out = {};
        for (const k of Object.keys(r)) {
          const low = k.toString().trim().toLowerCase();
          if (low === 'firstname' || low === 'first name' || low === 'first') out.firstName = String(r[k]).trim();
          else if (low === 'lastname' || low === 'last name' || low === 'last') out.lastName = String(r[k]).trim();
          else if (low === 'team') out.team = String(r[k]).trim();
          else if (low === 'jerseynumber' || low === 'jersey' || low === 'jersey #' || low === '#') out.jerseyNumber = parseInt(r[k] || 0, 10) || 0;
          else if (low === 'position' || low === 'pos') out.position = String(r[k]).trim();
          else if (low === 'shot' || low === 'shoots' || low === 'shots') out.shoots = String(r[k]).trim();
          else if (low === 'agegroup' || low === 'age' || low === 'age group') out.ageGroup = String(r[k]).trim();
        }
        return out;
      }).filter(r => r.firstName || r.lastName);
      setRows(norm);
      if (norm.length === 0) toast({ title: 'No rows found', description: 'Check your file uses the template columns.' });
    } catch (err) {
      toast({ title: 'Could not parse file', description: err.message || 'Please use the .xlsx template.' });
    } finally {
      setParsing(false);
    }
  };

  const submit = async () => {
    if (!rows.length) return;
    setUploading(true);
    try {
      const res = await PlayersAPI.bulk(rows);
      setResult(res);
      toast({
        title: 'Import complete',
        description: `${res.created} added, ${res.updated} updated${res.errors.length ? `, ${res.errors.length} errors` : ''}.`,
      });
      onImported && onImported();
    } catch (e) {
      toast({ title: 'Import failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white border-slate-200 max-w-xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 font-display">Bulk Import Players</DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Upload an .xlsx roster to add or update players in one shot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Upload an Excel file (.xlsx) to add or update players in one shot. New rows are added; existing
            players (matched by <b>first name + last name + team</b>) are updated.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadTemplate}
              data-testid="bulk-download-template"
              className="border-slate-200 text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4 mr-1.5" /> Download template
            </Button>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 cursor-pointer"
              data-testid="bulk-choose-file">
              <Upload className="h-4 w-4" /> {fileName ? 'Change file' : 'Choose file'}
              <input ref={inputRef} type="file" accept=".xlsx"
                onChange={handleFile} className="hidden" data-testid="bulk-file-input" />
            </label>
            {fileName && <span className="text-xs text-slate-500 self-center">{fileName}</span>}
          </div>

          {parsing && <div className="text-sm text-slate-500">Parsing</div>}

          {rows.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="bulk-preview-table">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 flex justify-between">
                <span>{rows.length} row{rows.length !== 1 ? 's' : ''} ready</span>
                <span className="text-slate-400">Preview (first 5)</span>
              </div>
              <div className="max-h-52 overflow-auto text-xs">
                <table className="w-full">
                  <thead className="bg-white text-slate-500">
                    <tr>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Team</th>
                      <th className="text-left p-2">Pos</th>
                      <th className="text-left p-2">Shot</th>
                      <th className="text-left p-2">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 text-slate-900">{r.firstName} {r.lastName}</td>
                        <td className="p-2 text-slate-600">{r.team}</td>
                        <td className="p-2 text-slate-600">{r.position}</td>
                        <td className="p-2 text-slate-600">{r.shoots}</td>
                        <td className="p-2 text-slate-600">{r.ageGroup}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-2" data-testid="bulk-result">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> {result.created} added, {result.updated} updated
              </div>
              {result.errors?.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-md p-2 text-xs text-amber-900 max-h-32 overflow-auto">
                  <div className="flex items-center gap-1 font-semibold mb-1">
                    <AlertCircle className="h-3.5 w-3.5" /> {result.errors.length} skipped
                  </div>
                  {result.errors.map((e, i) => (
                    <div key={i}>Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            data-testid="bulk-close"
            className="text-slate-600 hover:bg-slate-100">Close</Button>
          <Button onClick={submit} disabled={!rows.length || uploading}
            data-testid="bulk-submit"
            className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
            {uploading ? 'Importing' : `Import ${rows.length || ''} players`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
